import {AfterViewInit, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UserRoleDTO} from '../../../../clm/models/user/UserRoleDTO';
import {debounceTime, finalize, take, takeUntil} from 'rxjs/operators';
import {UserDTO} from '../../../../clm/models/user/UserDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {UserService} from '../../../services/user.service';
import {CustomValidator} from '../../../../common';
import {HelpInfo} from '../../../../clm/types/help-info.enum';
import {Role} from '../../../../clm/types/role.enum';
import {Subject} from 'rxjs';
import {Common} from '../../../../common/util/Util';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {BrokerDTO} from '../../../../onboarding-admin-portal/models/broker/BrokerDTO';
import {BrokerService} from '../../../../onboarding-admin-portal/services/broker.service';
import {ProviderService} from '../../../../ent/services/provider.service';
import {ProviderDTO} from '../../../../ent/models/ProviderDTO';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';

enum Controls {
  UserId = 'userId',
  FirstName = 'firstName',
  LastName = 'lastName',
  KeycloakId = 'keycloakId',
  EmailAddress = 'emailAddress',
  isActive = 'isActive',
  UserRoles = 'userRoles',
  LinkedEntityCode = 'linkedEntityCode'
}

@Component({
  selector: 'app-user-dialog',
  templateUrl: './add-user-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss',
    './add-user-dialog.component.scss'
  ]
})
export class AddUserDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly controls = Controls;
  readonly destroyed$ = new Subject<boolean>();
  readonly debounceTime = Common.HALF_SECOND_DEBOUNCE;

  dialogData: any;
  heading = 'Add User';
  isBusyCounter = 0;
  formGroup: FormGroup;
  errorMessage = '';
  allUserRoles: UserRoleDTO [] = [];
  isEditMode: boolean;
  selectedUser: UserDTO;
  loggedInUserName: string;
  linkedEntityTextChanged: Subject<string> = new Subject<string>();
  linkedEntities: NameValueCodeDTO[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AddUserDialogComponent>,
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private userService: UserService,
    private brokerService: BrokerService,
    private providerService: ProviderService
  ) {
    this.dialogData = data;
  }

  get userRolesFormAbstractControl(): AbstractControl {
    return this.formGroup.get(Controls.UserRoles);
  }

  get emailFormAbstractControl(): AbstractControl {
    return this.formGroup.get(Controls.EmailAddress);
  }

  ngOnInit(): void {
    this.getHelpInfo(HelpInfo.User);
    this.initFormGroup();
    this.initSearchTextChangedSubscriptions();
    this.loggedInUserName = this.sharedService.getLoggedInUserName();
  }

  ngAfterViewInit(): void {
    const {userId, userRoles, selectedUser} = this.dialogData;

    if (userId) {
      this.isEditMode = true;
      this.heading = 'Edit User';
      this.selectedUser = selectedUser;
      this.setFormsForEdit(selectedUser);
    }

    if (userRoles && userRoles?.length) {
      this.allUserRoles = userRoles;
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initFormGroup(): void {
    this.formGroup = this.formBuilder.group({
      [Controls.FirstName]: new FormControl(null, [Validators.required]),
      [Controls.LastName]: new FormControl(null, [Validators.required]),
      [Controls.KeycloakId]: new FormControl(null, [Validators.required]),
      [Controls.EmailAddress]: new FormControl(null, [Validators.required, CustomValidator.email]),
      [Controls.isActive]: new FormControl(true, [Validators.required]),
      [Controls.UserRoles]: new FormControl([], [CustomValidator.atLeastOneValue]),
      [Controls.LinkedEntityCode]: new FormControl(),
    });
  }

  initSearchTextChangedSubscriptions(): void {
    this.linkedEntityTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        if (value) {
          this.searchLinkedEntities(value);
        }
      });
  }

  getHelpInfo(componentName: string): void {
    this.sharedService.getHelpInfo(componentName);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  saveUser($event: MouseEvent): void {
    $event.stopPropagation();

    const userDTOPayload: UserDTO = this.getUserDTOPayload();
    this.isBusyCounter++;
    if (this.selectedUser?.requiresApproval === 1) {
      this.approveUserPayload(userDTOPayload);
    } else {
      this.saveUserPayload(userDTOPayload);
    }
  }

  private saveUserPayload(userDTOPayload: UserDTO) {
    this.userService.saveUser(userDTOPayload)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounter--)
      )
      .subscribe(
        (_) => {
          if (this.isEditMode) {
            this.sharedService.openSnackBar(`User ${userDTOPayload.userId} updated successfully`, 'Dismiss');
          } else {
            this.sharedService.openSnackBar(`The user has been added and a registration email has been emailed to the user`, 'Dismiss', 30000);
          }
          this.dialogRef.close({refetch: true});
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private approveUserPayload(userDTOPayload: UserDTO) {
    this.userService.approveUser(userDTOPayload)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounter--)
      )
      .subscribe(
        (_) => {
          this.sharedService.openSnackBar(`User ${userDTOPayload.userId} approved successfully`, 'Dismiss');
          this.dialogRef.close({refetch: true});
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  onRolesSelectionChange(selectedRoles: string[]): void {
    const linkedEntityControl = this.formGroup.get(Controls.LinkedEntityCode) as FormControl;
    if (this.isExternalRoleSelected(selectedRoles)) {
      linkedEntityControl.setValidators(Validators.required);
    } else {
      linkedEntityControl.patchValue(null);
      linkedEntityControl.clearValidators();
    }
    linkedEntityControl.updateValueAndValidity({ emitEvent: false });
  }

  showLinkedEntity(): boolean {
    const selectedRoles: string[] = this.formGroup.get(Controls.UserRoles).value;
    return this.isExternalRoleSelected(selectedRoles);
  }

  onInputChangedLinkedEntity($event: Event): void {
    const searchText = ($event.target as HTMLInputElement).value;
    this.linkedEntityTextChanged.next(searchText);
  }

  onLinkedEntityBlur(): void {
    const linkedEntityControl = this.formGroup.get(Controls.LinkedEntityCode);
    const currentValue = linkedEntityControl?.value;
    if (!currentValue || !this.linkedEntities || !this.linkedEntities.length) {
      linkedEntityControl?.patchValue(null);
      return;
    }

    const hasMatch = this.linkedEntities.some(entity =>
      entity.code?.toLowerCase() === currentValue?.toLowerCase()
    );

    if (!hasMatch) {
      linkedEntityControl?.patchValue(null);
    }
  }

  getLinkedEntityDisplayString(linkedEntity: NameValueCodeDTO): string {
    if (!linkedEntity) {
      return '';
    }
    return `${linkedEntity.code}: ${linkedEntity.name}`;
  }

  private setFormsForEdit(userDTO: UserDTO): void {
    const {
      keycloakId,
      firstName,
      lastName,
      emailAddress,
      isActive,
      linkedEntityCode,
      userRoles
    } = userDTO;

    const selectedRoles = userRoles.map((item: UserRoleDTO) => item.role_Name);
    this.formGroup.patchValue({
      [Controls.FirstName]: firstName,
      [Controls.LastName]: lastName,
      [Controls.KeycloakId]: keycloakId,
      [Controls.EmailAddress]: emailAddress,
      [Controls.isActive]: isActive,
      [Controls.LinkedEntityCode]: linkedEntityCode,
      [Controls.UserRoles]: selectedRoles
    });

    this.onRolesSelectionChange(selectedRoles);
  }

  private getUserDTOPayload(): UserDTO {
    let payload: UserDTO;

    payload = {
      created: this.isEditMode ? this.selectedUser.created : new Date(),
      createdBy: this.isEditMode ? this.selectedUser.createdBy : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      [Controls.UserId]: this.isEditMode ? this.selectedUser.userId : null,
      [Controls.FirstName]: this.formGroup.get(Controls.FirstName).value,
      [Controls.LastName]: this.formGroup.get(Controls.LastName).value,
      [Controls.KeycloakId]: this.formGroup.get(Controls.KeycloakId).value,
      [Controls.EmailAddress]: this.formGroup.get(Controls.EmailAddress).value,
      [Controls.isActive]: this.formGroup.get(Controls.isActive).value ? 1 : 0,
      [Controls.LinkedEntityCode]: this.formGroup.get(Controls.LinkedEntityCode).value,
      [Controls.UserRoles]: this.formGroup.get(Controls.UserRoles).value.map((role: string) => ({
        role_Name: role,
        is_composite: this.allUserRoles.find((userRole: UserRoleDTO) => userRole.role_Name === role)?.is_composite ?? false,
      }))
    } as UserDTO;
    return payload;
  }

  private isExternalRoleSelected(selectedRoles: string[]) {
    return selectedRoles.includes(Role.Broker) || selectedRoles.includes(Role.Provider);
  }

  private searchLinkedEntities(searchValue: string): void {
    const selectedRoles: string[] = this.formGroup.get(Controls.UserRoles).value;
    if (selectedRoles.includes(Role.Broker)) {
      this.searchBrokers(searchValue);
    } else if (selectedRoles.includes(Role.Provider)) {
      this.searchProviders(searchValue);
    }
  }

  private searchBrokers(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblBroker',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {}
    };

    const fields = [
      {key: 'name', name: 'name', logicGate: 'AND', bracket: '('},
      {key: 'brokerNumber', name: 'brokerNumber', logicGate: 'OR', bracket: ')'}
    ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: searchValue,
        logicGate: field.logicGate,
        bracket: field.bracket || ''
      };
    });

    this.brokerService.getListOfBrokers(pagedRequestDTO)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (response: PagedResponseDTO<BrokerDTO>) => {
          this.linkedEntities = response.resultList.map(broker => ({
            name: broker.name,
            value: broker.brokerId,
            code: broker.brokerNumber
          }));
        },
        error: (error) => console.error('Error fetching brokers:', error)
      });
  }

  private searchProviders(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblProvider',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {}
    };

    const fields = [
      {key: 'name', name: 'name', logicGate: 'AND', bracket: '('},
      {key: 'practiceNumber', name: 'practiceNumber', logicGate: 'OR', bracket: ')'}
    ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: searchValue,
        logicGate: field.logicGate,
        bracket: field.bracket || ''
      };
    });

    this.providerService.getListOfProviders(pagedRequestDTO)
      .pipe(
        take(1)
      )
      .subscribe({
        next: (response: PagedResponseDTO<ProviderDTO>) => {
          this.linkedEntities = response.resultList.map(provider => ({
            name: provider.name,
            value: provider.providerId,
            code: provider.practiceNumber
          }));
        },
        error: (error) => console.error('Error fetching providers:', error)
      });
  }
}
